// src/views/LobbyView.jsx
import React, { useState } from 'react';
import {
  Book,
  GraduationCap,
  Sparkles,
  Box,
  Lock,
  ChevronRight,
  ClipboardCheck,
  X,
  Zap,
  Target
} from 'lucide-react';
import Mascot from '../components/Mascot';
import PurchaseSuccessModal from '../components/modals/PurchaseSuccessModal';
import { useLobbyLogic } from '../hooks/useLobbyLogic';
import { WEEKLY_CHALLENGE } from '../data/weeklyChallenge';
import { WEEKLY_QUIZ } from '../data/weeklyQuiz';
import { CEFR_LEVELS } from '../data/grammarQuestData';


const SECTIONS = [
  { id: 'modules_base',  title: 'Ruta Principal', subtitle: 'Módulos y Unidades',    icon: <Book size={28} />,          view: 'modules',    color: 'bg-blue-600',  cost: 0 },
  { id: 'grammar_base',  title: 'Gramática',       subtitle: 'Biblioteca de consulta',icon: <GraduationCap size={28} />, view: 'grammar',    color: 'bg-blue-500',  cost: 0 },
  { id: 'vocabulary_hub',title: 'Vocabulario',     subtitle: 'Palabras y frases',     icon: <Sparkles size={28} />,      view: 'vocabulary', color: 'bg-sky-500',   cost: 0 },
  { id: 'surprise_chest',title: 'Zona Misteriosa', subtitle: 'Sets sorpresa',         icon: <Box size={28} />,           view: 'mysterious', color: 'bg-amber-500', cost: 100 },
];


// Grammar Quest — sección separada con acceso maestro o 500 XP
// Niveles disponibles tomados de los datos del juego (no hardcodeados)
const QUEST_AVAILABLE_LEVELS = CEFR_LEVELS.filter((l) => l.available).map((l) => l.id);
const QUEST_LEVEL_LABEL =
  QUEST_AVAILABLE_LEVELS.length > 1
    ? `${QUEST_AVAILABLE_LEVELS[0]}–${QUEST_AVAILABLE_LEVELS[QUEST_AVAILABLE_LEVELS.length - 1]}`
    : (QUEST_AVAILABLE_LEVELS[0] || '');

// Grammar Quest — sección separada con acceso maestro o 500 XP
const GRAMMAR_QUEST_SECTION = {
  id: 'grammar_quest',
  title: 'Grammar Word Quest',
  subtitle: QUEST_LEVEL_LABEL ? `Mini reto gramatical ${QUEST_LEVEL_LABEL}` : 'Mini reto gramatical',
  view: 'grammar-quest',
  cost: 500,
};

const WEEKLY_DISMISSED_KEY = 'educat_dismissed_weekly_quiz';


const activeWeekly = WEEKLY_CHALLENGE || WEEKLY_QUIZ;


const wasWeeklyDismissed = () => {
  try {
    return localStorage.getItem(WEEKLY_DISMISSED_KEY) === activeWeekly?.id;
  } catch {
    return false;
  }
};


// Etiqueta de sección (solo visual)
function SectionLabel({ children }) {
  return (
    <p className="px-1 text-[11px] font-black uppercase tracking-widest text-slate-400">
      {children}
    </p>
  );
}


export default function LobbyView({
  setView,
  userProgress,
  setActiveModal,
  setItemToBuy,
  onStartWeeklyQuiz, // opcional: se conectará al modal o al inicio del set
  isTeacherMode,
}) {
  const { isJumping, triggerJump, checkAccess } = useLobbyLogic(userProgress);
  const activeSkin = userProgress.currentSkin || 'bribark';


  const [showWeeklyBubble, setShowWeeklyBubble] = useState(
    () => activeWeekly?.isActive && !wasWeeklyDismissed()
  );


  const dismissWeeklyBubble = () => {
    try {
      if (activeWeekly?.id) localStorage.setItem(WEEKLY_DISMISSED_KEY, activeWeekly.id);
    } catch {
      // Si localStorage no está disponible, solo se oculta en esta sesión
    }
    setShowWeeklyBubble(false);
  };


  const handleOpenWeeklyQuiz = () => {
    if (onStartWeeklyQuiz) {
      onStartWeeklyQuiz(activeWeekly);
    }
  };


  // ✅ Lógica original de compra — sin cambios
  const handleLockedSection = (section) => {
    setActiveModal({
      type: 'CONFIRM_PURCHASE',
      data: {
        id: section.id,
        title: section.title,
        subtitle: section.subtitle,
        cost: section.cost,
        icon: '🔮',
        description: 'Desbloquea sets de vocabulario exclusivos y skins secretas.'
      }
    });
  };


  // ✅ Acceso al Grammar Quest — modo maestro O 500 XP
  const hasGrammarQuestAccess = isTeacherMode ||
    (userProgress?.unlockedContent || []).includes(GRAMMAR_QUEST_SECTION.id);


  const handleGrammarQuestEntry = () => {
    if (hasGrammarQuestAccess) {
      setView(GRAMMAR_QUEST_SECTION.view);
    } else {
      setActiveModal({
        type: 'CONFIRM_PURCHASE',
        data: {
          id: GRAMMAR_QUEST_SECTION.id,
          title: GRAMMAR_QUEST_SECTION.title,
          subtitle: GRAMMAR_QUEST_SECTION.subtitle,
          cost: GRAMMAR_QUEST_SECTION.cost,
          icon: '🎯',
          description: 'Desbloquea el mini-reto gramatical interactivo con rueda de palabras, colección y reto final.'
        }
      });
    }
  };


  // Tarjeta compacta reutilizable para Gramática y Vocabulario
  const renderToolCard = (section) => {
    const hasAccess = checkAccess(section);
    return (
      <button
        key={section.id}
        onClick={() => hasAccess ? setView(section.view) : handleLockedSection(section)}
        className={`p-4 rounded-3xl border-2 border-b-4 flex flex-col items-start gap-3 text-left transition-all active:scale-95 active:border-b-0
          ${hasAccess
            ? 'bg-white border-blue-100 shadow-sm hover:border-blue-300 hover:shadow-md'
            : 'bg-slate-50 border-slate-200 border-dashed'
          }`}
      >
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-sm
          ${hasAccess ? section.color : 'bg-slate-200 text-slate-400'}`}>
          {hasAccess ? section.icon : <Lock size={24} />}
        </div>
        <div>
          <p className={`font-black text-base leading-tight ${hasAccess ? 'text-slate-800' : 'text-slate-500'}`}>
            {section.title}
          </p>
          <p className="text-xs font-medium text-slate-500 mt-0.5">
            {section.subtitle}
          </p>
        </div>
        {!hasAccess && (
          <span className="text-[11px] font-black bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">
            Desbloquear · {section.cost} XP
          </span>
        )}
      </button>
    );
  };


  const mysteryZone = SECTIONS[3];
  const mysteryAccess = checkAccess(mysteryZone);


  return (
    <div className="space-y-6 pb-28 animate-in fade-in duration-700">


      {/* MASCOTA + ENCABEZADO */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div
            className="hover:scale-110 transition-transform cursor-pointer"
            onClick={() => {
              triggerJump();
              setTimeout(() => setActiveModal({ type: 'XP_STORE' }), 300);
            }}
          >
            <Mascot type={activeSkin} isJumping={isJumping} />
          </div>
        </div>
        <div>
          <h1 className="text-3xl font-black text-slate-800">¿Qué aprenderemos hoy?</h1>
          <p className="text-slate-500 font-bold tracking-tight">Continúa tu camino o prueba un reto</p>
        </div>
      </div>


      {/* TU APRENDIZAJE — Ruta Principal */}
      <section className="space-y-3">
        <SectionLabel>Tu aprendizaje</SectionLabel>
        {(() => {
          const main = SECTIONS[0];
          const hasAccess = checkAccess(main);
          const completedSets = userProgress?.completedSets?.length || 0;
          const totalEstimated = 80;
          const progressPct = Math.min((completedSets / totalEstimated) * 100, 100);


          return (
            <div className="rounded-[2rem] overflow-hidden shadow-xl border-b-4 border-blue-900 relative bg-gradient-to-br from-blue-500 via-blue-600 to-blue-800">
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />


              <div className="relative z-10 p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-[11px] font-black uppercase tracking-widest">Ruta Principal</p>
                    <h3 className="text-white font-black text-2xl leading-tight">Tu camino al inglés</h3>
                  </div>
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-white shadow-inner">
                    <Book size={28} />
                  </div>
                </div>


                {completedSets > 0 ? (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs font-bold text-blue-100">
                      <span>{completedSets} sets completados</span>
                      <span>{Math.round(progressPct)}%</span>
                    </div>
                    <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-sky-200 to-white rounded-full transition-all duration-700"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm font-medium text-blue-100">
                    Empieza tu primer set hoy y construye tu progreso.
                  </p>
                )}


                <button
                  onClick={() => setView(main.view)}
                  className="w-full py-4 rounded-2xl font-black text-blue-700 text-sm bg-white shadow-lg hover:bg-blue-50 active:scale-95 transition-all border-b-4 border-blue-200 flex items-center justify-center gap-2"
                >
                  {completedSets > 0 ? 'Continuar aprendiendo' : 'Comenzar mi ruta'}
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          );
        })()}
      </section>


      {/* RETOS — Desafío semanal + Grammar Word Quest */}
      <section className="space-y-3">
        <SectionLabel>Retos</SectionLabel>


        {/* DESAFÍO SEMANAL */}
        {activeWeekly?.isActive && showWeeklyBubble && (
          <div className="relative rounded-3xl border-2 border-blue-100 bg-white p-4 text-left shadow-md animate-in fade-in slide-in-from-top-2 duration-500">
            <button
              onClick={dismissWeeklyBubble}
              aria-label="Ocultar desafío semanal"
              className="absolute right-3 top-3 rounded-full p-1 text-slate-300 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={14} />
            </button>


            <div className="flex items-start gap-3 pr-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-sm">
                <ClipboardCheck size={24} />
              </div>


              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-black uppercase tracking-widest text-blue-600">
                  Desafío semanal · Esta semana
                </p>
                <p className="text-base font-black leading-snug text-slate-800 mt-0.5">
                  {activeWeekly.title}
                </p>
                <p className="mt-1 text-xs font-medium text-slate-500">
                  {activeWeekly.sections ? `${activeWeekly.sections.length} mini retos` : `${activeWeekly.totalQuestions} preguntas`} · ~{activeWeekly.estimatedMinutes} min
                </p>
              </div>
            </div>


            <button
              onClick={handleOpenWeeklyQuiz}
              className="mt-4 w-full rounded-2xl border-b-4 border-blue-800 bg-blue-600 py-3 text-sm font-black text-white transition hover:bg-blue-700 active:translate-y-0.5 active:border-b-0"
            >
              {activeWeekly.sections ? 'Explorar desafío' : 'Comenzar quiz'}
            </button>
          </div>
        )}


        {/* GRAMMAR WORD QUEST */}
        <button
          onClick={handleGrammarQuestEntry}
          className={`w-full rounded-3xl border-2 border-b-4 p-4 text-left transition-all active:scale-[0.98] active:border-b-0
            ${hasGrammarQuestAccess
              ? 'bg-white border-blue-100 shadow-md hover:border-blue-300'
              : 'bg-white border-slate-200 border-dashed'
            }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-sm
                ${hasGrammarQuestAccess ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}
            >
              {hasGrammarQuestAccess ? <Target size={24} /> : <Lock size={22} />}
            </div>


            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-black text-base leading-tight text-slate-800">
                  {GRAMMAR_QUEST_SECTION.title}
                </p>
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full shrink-0 bg-blue-50 text-blue-600 border border-blue-100">
                  {QUEST_LEVEL_LABEL && (
  <span className="text-[10px] font-black px-2 py-0.5 rounded-full shrink-0 bg-blue-50 text-blue-600 border border-blue-100">
    {QUEST_LEVEL_LABEL}
  </span>
)}
                </span>
              </div>
              <p className="text-xs font-medium text-slate-500 mt-0.5">
                {GRAMMAR_QUEST_SECTION.subtitle}
              </p>
            </div>


            <div className="shrink-0">
              {hasGrammarQuestAccess ? (
                <div className="flex items-center gap-1 px-3 py-2 rounded-xl font-black text-xs bg-blue-600 text-white">
                  Entrar
                  <ChevronRight size={14} />
                </div>
              ) : (
                <div className="flex items-center gap-1 bg-amber-100 text-amber-700 px-3 py-2 rounded-xl font-black text-xs border border-amber-200">
                  <Zap size={12} />
                  Desbloquear · {GRAMMAR_QUEST_SECTION.cost} XP
                </div>
              )}
            </div>
          </div>
        </button>
      </section>


      {/* EXPLORA Y PRACTICA — Gramática + Vocabulario + Zona Misteriosa */}
      <section className="space-y-3">
        <SectionLabel>Explora y practica</SectionLabel>


        <div className="grid grid-cols-2 gap-3">
          {renderToolCard(SECTIONS[1])}
          {renderToolCard(SECTIONS[2])}
        </div>


        <button
          onClick={() => mysteryAccess ? setView(mysteryZone.view) : handleLockedSection(mysteryZone)}
          className={`w-full rounded-2xl border-2 p-3 flex items-center gap-3 text-left transition-all active:scale-[0.98]
            ${mysteryAccess
              ? 'bg-white border-amber-100 shadow-sm hover:border-amber-300'
              : 'bg-slate-50 border-slate-200 border-dashed'
            }`}
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0
            ${mysteryAccess ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
            {mysteryAccess ? <Box size={20} /> : <Lock size={18} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-black text-sm leading-tight ${mysteryAccess ? 'text-slate-800' : 'text-slate-500'}`}>
              {mysteryZone.title}
            </p>
            <p className="text-xs font-medium text-slate-500">{mysteryZone.subtitle}</p>
          </div>
          {!mysteryAccess ? (
            <span className="shrink-0 text-[11px] font-black bg-amber-100 text-amber-700 px-2 py-1 rounded-full border border-amber-200">
              {mysteryZone.cost} XP
            </span>
          ) : (
            <ChevronRight size={18} className="text-slate-300 shrink-0" />
          )}
        </button>
      </section>


      {/* TU NIVEL DE INGLÉS */}
      <section className="space-y-3">
        <SectionLabel>Tu nivel de inglés</SectionLabel>


        {!userProgress?.cefrLevel && (
          <button
            onClick={() => setView('placement-select')}
            className="w-full bg-white rounded-3xl p-4 flex items-center gap-4 shadow-md border-2 border-b-4 border-blue-100 hover:border-blue-300 active:scale-[0.99] active:border-b-2 transition-all"
          >
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <Target size={24} />
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-black text-slate-800">¿Cuál es tu nivel de inglés?</p>
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 shrink-0">
                  Gratis
                </span>
              </div>
              <p className="text-xs font-medium text-slate-500 mt-0.5">Test rápido A1–C2 · 5 minutos</p>
            </div>
            <ChevronRight size={20} className="text-slate-300 shrink-0" />
          </button>
        )}


        {userProgress?.cefrLevel && (
          <button
            onClick={() => setView('placement-select')}
            className="w-full bg-white rounded-2xl p-3 flex items-center gap-3 shadow-sm border-2 border-blue-100 hover:border-blue-300 hover:shadow-md transition-all"
          >
            <div className="w-10 h-10 rounded-2xl bg-blue-100 flex items-center justify-center">
              <span className="text-lg font-black text-blue-600">{userProgress.cefrLevel}</span>
            </div>
            <div className="flex-1 text-left">
              <p className="text-xs font-black text-blue-600">Tu nivel CEFR</p>
              <p className="text-[11px] text-slate-500">{userProgress.cefrTestDate} · Toca para repetir el test</p>
            </div>
            <ChevronRight size={18} className="text-slate-300" />
          </button>
        )}
      </section>


    </div>
  );
}